/**
 * Suggest Pattern Tool
 *
 * Recommends patterns for common use cases.
 */

import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import patternLibrary from '../data/pattern-library.json' with { type: 'json' };
import type { PatternLibrary } from '../types.js';

const patterns = patternLibrary as unknown as PatternLibrary;

const SuggestPatternSchema = z.object({
  useCase: z
    .enum([
      // Layout patterns
      'dashboard-page',
      'public-page',
      'auth-page',
      'error-page',
      'form-page',
      // Form patterns
      'login-form',
      'registration-form',
      'contact-form',
      // MFA and password form patterns
      'mfa-verification-form',
      'mfa-setup-form',
      'profile-update-form',
      'change-password-form',
      'forgot-password-form',
      'reset-password-form',
      // Auth flow patterns
      'login-with-mfa',
      'sso-login',
      'session-management',
      'protected-route',
      // Notification patterns
      'notification-center',
      'notification-dropdown',
    ])
    .describe('The use case you need a pattern for'),
  requirements: z.array(z.string()).optional().describe('Additional requirements or constraints'),
});

/**
 * Register the suggest_pattern tool
 */
export function registerSuggestPatternTool(server: McpServer): void {
  server.tool(
    'suggest_pattern',
    'Get recommended patterns for common use cases',
    SuggestPatternSchema.shape,
    async (params) => {
      const { useCase, requirements } = SuggestPatternSchema.parse(params);

      // Find matching layout, form, auth, or notification pattern
      const layout = patterns.layouts.find((l) => l.id === useCase || l.useCase === useCase);
      const form = patterns.forms.find((f) => f.id === useCase);
      const auth = patterns.auth?.find((a) => a.id === useCase);
      const notification = patterns.notifications?.find((n) => n.id === useCase);
      const pattern = layout || form || auth || notification;

      if (!pattern) {
        // Suggest available patterns
        const availablePatterns = [
          ...patterns.layouts.map((l) => ({ id: l.id, type: 'layout', name: l.name })),
          ...patterns.forms.map((f) => ({ id: f.id, type: 'form', name: f.name })),
          ...(patterns.auth?.map((a) => ({ id: a.id, type: 'auth', name: a.name })) || []),
          ...(patterns.notifications?.map((n) => ({
            id: n.id,
            type: 'notification',
            name: n.name,
          })) || []),
        ];

        return {
          content: [
            {
              type: 'text' as const,
              text: JSON.stringify(
                {
                  error: `No pattern found for use case "${useCase}"`,
                  availablePatterns,
                  hint: 'Use one of the available pattern IDs',
                },
                null,
                2
              ),
            },
          ],
        };
      }

      return {
        content: [
          {
            type: 'text' as const,
            text: JSON.stringify(
              {
                pattern,
                requirements: requirements || [],
                implementationTips: [
                  'Import components from @classic-homes/theme-svelte',
                  'Use the provided example as a starting point',
                  'Customize props based on your specific requirements',
                  'Follow accessibility guidelines in component documentation',
                ],
              },
              null,
              2
            ),
          },
        ],
      };
    }
  );
}
